import React, { useEffect, useRef, useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import {
    HeroSection,
    HeroContent,
    HeroTitle,
    HeroSubtitle,
    HeroButtonWrapper,
    SectionTitle,
    FeatureSection,
    FeatureCard,
    FeatureIcon,
    FeatureCardIcon,
    SectionSubtitle,
    SectionBadge,
    FeatureButtonWrapper,
    StatsSection,
    StatsTitle,
    StatsSubtitle,
    StatCard,
    StatNumber,
    StatLabel,
    StatsButtonWrapper,
    FeatureCardsWrapper,
    FeatureList,
    FeatureListItem,
    FeatureListItemLabel,
    FeatureDescription,
    AppSection,
    AppCard,
    AppCardsWrapper,
    AppScreen,
    AppDescription,
    AppTitle,
    AppOverlay,
    IndicatorDot,
    IndicatorWrapper,
    AppScreenBackground,
    ClickableList,
    ClickableListItem,
    ItemDescription,
    ItemTitle,
    AppButtonWrapper,
    AppSectionBackground,
    FeatureTitle,
    ManagdInNumbersCard,
    HowItWorksSection,
    HowItWorksStepsWrapper,
    HowItWorksDesktopLine,
    HowItWorksMobileLine,
    HowItWorksStep,
    HowItWorksIconWrapper,
    HowItWorksStepBox,
    HowItWorksStepContent,
    HowItWorksStepTitle,
    HowItWorksStepDescription,
    AppSectionCard,
    HeroBadge,
} from "./home-page.styles";
import RentalIcon from "../../../../assets/images/landing-page/rental-icon.webp";
import RentalManagementIcon from "../../../../assets/images/landing-page/rental-management-icon.webp";
import InspectionIcon from "../../../../assets/images/landing-page/inspection-icon.webp";
import RentalIncomeIcon from "../../../../assets/images/landing-page/rental-income-icon.webp";
import { useSwipeable } from "react-swipeable";
import { CSSTransition } from "react-transition-group";
import useIsMobile from "../../../../hooks/useIsMobile";
import TabBar from "../../components/tab-bar/tab-bar";
import CTAButton from "../../../../components/cta-button/cta-button";
import MaterialIcon from "../../../../components/material-icon";
import { colorPalette } from "../../../../utils/theme";

// Screenshots
import DashboardScreen from "../../../../assets/images/landing-page/dashboard-screen.webp";
import UnitScreen from "../../../../assets/images/landing-page/unit-screen.webp";
import FinanceScreen from "../../../../assets/images/landing-page/finance-screen.webp";
import ContractsScreen from "../../../../assets/images/landing-page/contracts-screen.webp";
import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import Footer from "../../components/footer/footer";
import Articles from "./components/articles/articles";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { MaterialSymbol } from "@material-symbols/font-400";
import ControlAnywhere from "./components/control-anywhere/control-anywhere";
import FAQSection, { getInvestorFAQItems } from "../../components/faq-section/faq-section";
import JsonLd from "../../../../components/json-ld/json-ld";

const getServiceItems = (t: any) => [
    {
        icon: RentalIcon,
        iconWidth: 110,
        iconHeight: 120,
        title: t('pages.home.services.marketing.title'),
        description: t('pages.home.services.marketing.description'),
        listItems: [
            {
                icon: "table_chart_view",
                title: t('pages.home.services.marketing.items.pricing_strategy'),
            },
            {
                icon: "home_pin",
                title: t('pages.home.services.marketing.items.portal_marketing'),
            },
            {
                icon: "location_away",
                title: t('pages.home.services.marketing.items.viewing_arrangement'),
            }
        ],
    },
    {
        icon: RentalManagementIcon,
        iconWidth: 110,
        iconHeight: 120,
        title: t('pages.home.services.management.title'),
        description: t('pages.home.services.management.description'),
        listItems: [
            {
                icon: "history_edu",
                title: t('pages.home.services.management.items.contracts_documents'),
            },
            {
                icon: "concierge",
                title: t('pages.home.services.management.items.personal_tenant_care'),
            },
            {
                icon: "balance",
                title: t('pages.home.services.management.items.eviction_measures'),
            },
        ],
    },
    {
        icon: InspectionIcon,
        iconWidth: 110,
        iconHeight: 120,
        title: t('pages.home.services.maintenance.title'),
        description: t('pages.home.services.maintenance.description'),
        listItems: [
            {
                icon: "wifi_home",
                title: t('pages.home.services.maintenance.items.move_inspections'),
            },
            {
                icon: "handyman",
                title: t('pages.home.services.maintenance.items.repair_coordination'),
            },
            {
                icon: "manage_accounts",
                title: t('pages.home.services.maintenance.items.warranty_management'),
            }
        ],
    },
    {
        icon: RentalIncomeIcon,
        iconWidth: 103,
        iconHeight: 120,
        title: t('pages.home.services.financial.title'),
        description: t('pages.home.services.financial.description'),
        listItems: [
            {
                icon: "checkbook",
                title: t('pages.home.services.financial.items.rent_check_management'),
            },
            {
                icon: "open_in_new",
                title: t('pages.home.services.financial.items.rental_income_payout'),
            },
            {
                icon: "reset_wrench",
                title: t('pages.home.services.financial.items.service_charge_coordination'),
            }
        ],
    },
];

const getAppItems = (t: TFunction) => [
    {
        id: 1,
        title: t('pages.home.app.dashboard.title'),
        description: t('pages.home.app.dashboard.description'),
        image: DashboardScreen,
    },
    {
        id: 2,
        title: t('pages.home.app.units.title'),
        description: t('pages.home.app.units.description'),
        image: UnitScreen,
    },
    {
        id: 3,
        title: t('pages.home.app.finance.title'),
        description: t('pages.home.app.finance.description'),
        image: FinanceScreen,
    },
    {
        id: 4,
        title: t('pages.home.app.contracts.title'),
        description: t('pages.home.app.contracts.description'),
        image: ContractsScreen,
    },
];

const getHowItWorksSteps = (t: TFunction) => [
    {
        icon: "add_home_work" as const,
        title: t('pages.home.how_it_works.steps.register_unit.title'),
        description: t('pages.home.how_it_works.steps.register_unit.description'),
    },
    {
        icon: "file_export" as const,
        title: t('pages.home.how_it_works.steps.verify_account.title'),
        description: t('pages.home.how_it_works.steps.verify_account.description'),
    },
    {
        icon: "list_alt_check" as const,
        title: t('pages.home.how_it_works.steps.select_package.title'),
        description: t('pages.home.how_it_works.steps.select_package.description'),
    },
    {
        icon: "real_estate_agent" as const,
        title: t('pages.home.how_it_works.steps.we_handle_rest.title'),
        description: t('pages.home.how_it_works.steps.we_handle_rest.description'),
    },
];

const HomePage: React.FC = () => {
    const isMobile = useIsMobile();
    const navigate = useCustomNavigate();
    const { t } = useTranslation();

    // Memoize service items to update when language changes
    const serviceItems = useMemo(() => getServiceItems(t), [t]);
    const howItWorksSteps = useMemo(() => getHowItWorksSteps(t), [t]);
    const appItems = useMemo(() => getAppItems(t), [t]);
    const faqItems = useMemo(() => getInvestorFAQItems(t), [t]);

    // References
    const featuresRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<HTMLDivElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);

    // States
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAppSceneTimerDeactived, setIsAppSceneTimerDeactived] = useState(false);
    const [featuresVisible, setFeaturesVisible] = useState(false);
    const [appVisible, setAppVisible] = useState(false);
    const [statsVisible, setStatsVisible] = useState(false);
    const [isHeroVisible, setIsHeroVisible] = useState(false);

    // Effects
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsHeroVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isAppSceneTimerDeactived && appVisible) {
            const interval = setInterval(() => {
                setActiveIndex((prevIndex) => (prevIndex + 1) % appItems.length);
            }, 4000); // Trigger every 4 seconds

            return () => clearInterval(interval);
        }
    }, [isAppSceneTimerDeactived, appVisible, appItems.length]);

    useEffect(() => {
        const currentStatsRef = statsRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setStatsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentStatsRef) {
            observer.observe(currentStatsRef);
        }

        return () => {
            if (currentStatsRef) observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const currentAppRef = appRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setAppVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentAppRef) {
            observer.observe(currentAppRef);
        }

        return () => {
            if (currentAppRef) observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const currentFeaturesRef = featuresRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setFeaturesVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentFeaturesRef) {
            observer.observe(currentFeaturesRef);
        }

        return () => {
            if (currentFeaturesRef) observer.disconnect();
        };
    }, []);

    // Handle swipe actions
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (activeIndex < appItems.length - 1) {
                setActiveIndex((prevIndex) => prevIndex + 1);
                setIsAppSceneTimerDeactived(true);
            }
        },
        onSwipedRight: () => {
            if (activeIndex > 0) {
                setActiveIndex((prevIndex) => prevIndex - 1);
                setIsAppSceneTimerDeactived(true);
            }
        },
        trackMouse: true, // Enables swipe with mouse drag for testing
    });

    return (
        <>
            <Row>
                <Col xs={12} md={11} xxl={10} className="mx-auto">
                    <TabBar />

                    {/* Hero Section */}
                    <HeroSection>
                        <CSSTransition in={isHeroVisible} timeout={800} classNames="slide" unmountOnExit>
                            <>
                                <Row className="align-items-center" style={isMobile ? undefined : { paddingLeft: 50, paddingRight: 50 }}>
                                    <Col xs={12} md={7} className="mx-auto">
                                        <HeroContent>
                                            <HeroBadge>{t('pages.home.hero.for_landlords')}</HeroBadge>
                                            <HeroTitle>{t('pages.home.hero.title')}</HeroTitle>
                                            <HeroSubtitle>
                                                {t('pages.home.hero.subtitle')}
                                            </HeroSubtitle>
                                            <HeroButtonWrapper>
                                                <CTAButton
                                                    variant="primary"
                                                    size="L"
                                                    label={t('buttons.all_benefits')}
                                                    onClick={() => navigate("/details#services")}
                                                    fullWidth
                                                />
                                            </HeroButtonWrapper>
                                        </HeroContent>
                                    </Col>
                                </Row>
                            </>
                        </CSSTransition>
                    </HeroSection>

                    {/* Control Anywhere Section */}
                    <ControlAnywhere />

                    {/* Features Section */}
                    <FeatureSection ref={featuresRef}>
                        <CSSTransition in={featuresVisible} timeout={800} classNames="slide" unmountOnExit>
                            <div>
                                <SectionBadge>{t('pages.home.features.badge')}</SectionBadge>
                                <SectionTitle>
                                    {t('pages.home.features.section_title')}
                                </SectionTitle>
                                <SectionSubtitle style={{ marginBottom: isMobile ? 0 : 32 }}>
                                    {t('pages.home.features.section_subtitle')}
                                </SectionSubtitle>

                                {isMobile ? (
                                    <FeatureCardsWrapper>
                                        {serviceItems.map((item, index) => (
                                            <FeatureCard key={index}>
                                                <FeatureIcon src={item.icon} alt="Service icon" draggable={false} />
                                                <FeatureTitle>{item.title}</FeatureTitle>
                                                <FeatureDescription>{item.description}</FeatureDescription>
                                                <FeatureList>
                                                    {item.listItems.map((listItem, subIndex) => (
                                                        <FeatureListItem key={subIndex}>
                                                            <MaterialIcon icon={listItem.icon as MaterialSymbol} size={16} color={colorPalette.blue500} />
                                                            <FeatureListItemLabel>{listItem.title}</FeatureListItemLabel>
                                                        </FeatureListItem>
                                                    ))}
                                                </FeatureList>
                                            </FeatureCard>
                                        ))}
                                    </FeatureCardsWrapper>
                                ) : (
                                    <Row className="gy-3 gx-3" style={{ marginBottom: 32 }}>
                                        {serviceItems.map((item, index) => (
                                            <Col md={6} key={index}>
                                                <FeatureCard>
                                                    <FeatureTitle>{item.title}</FeatureTitle>
                                                    <FeatureDescription>{item.description}</FeatureDescription>
                                                    <FeatureList>
                                                        {item.listItems.map((listItem, subIndex) => (
                                                            <FeatureListItem key={subIndex}>
                                                                <MaterialIcon icon={listItem.icon as MaterialSymbol} size={16} color={colorPalette.blue500} />
                                                                <FeatureListItemLabel>{listItem.title}</FeatureListItemLabel>
                                                            </FeatureListItem>
                                                        ))}
                                                    </FeatureList>
                                                    <FeatureCardIcon src={item.icon} alt="Service icon" draggable={false} width={item.iconWidth} height={item.iconHeight} />
                                                </FeatureCard>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                                <FeatureButtonWrapper>
                                    <CTAButton
                                        variant="primary"
                                        size="L"
                                        label={isMobile ? t('buttons.all_services') : t('buttons.all_services_packages')}
                                        onClick={() => navigate("/details#services")}
                                        fullWidth={isMobile}
                                    />
                                </FeatureButtonWrapper>
                            </div>
                        </CSSTransition>
                    </FeatureSection>

                    {/* How It Works Section */}
                    <HowItWorksSection>
                        <div style={{ textAlign: 'center' }}>
                            <SectionBadge>{t('pages.home.how_it_works.badge')}</SectionBadge>
                            <SectionTitle>
                                {t('pages.home.how_it_works.section_title')}
                            </SectionTitle>
                            <SectionSubtitle>
                                {t('pages.home.how_it_works.section_subtitle')}
                            </SectionSubtitle>

                            <HowItWorksStepsWrapper>
                                <HowItWorksDesktopLine />
                                <HowItWorksMobileLine />

                                {isMobile ? (
                                    <div>
                                        <HowItWorksMobileLine />
                                        {howItWorksSteps.map((step, index) => (
                                            <HowItWorksStep key={index}>
                                                <HowItWorksIconWrapper>
                                                    <MaterialIcon
                                                        icon={step.icon}
                                                        size={20}
                                                        color={colorPalette.white}
                                                    />
                                                </HowItWorksIconWrapper>
                                                <HowItWorksStepBox>
                                                    <HowItWorksStepTitle>{step.title}</HowItWorksStepTitle>
                                                    <HowItWorksStepDescription>{step.description}</HowItWorksStepDescription>
                                                </HowItWorksStepBox>
                                            </HowItWorksStep>
                                        ))}
                                    </div>
                                ) : (
                                    <Row className="gy-4 gx-2">
                                        {howItWorksSteps.map((step, index) => (
                                            <Col key={index} md={3}>
                                                <HowItWorksStep>
                                                    <HowItWorksStepBox>
                                                        <HowItWorksIconWrapper>
                                                            <MaterialIcon
                                                                icon={step.icon}
                                                                size={24}
                                                                color={colorPalette.white}
                                                            />
                                                        </HowItWorksIconWrapper>
                                                        <HowItWorksStepTitle>{step.title}</HowItWorksStepTitle>
                                                        <HowItWorksStepDescription>{step.description}</HowItWorksStepDescription>
                                                    </HowItWorksStepBox>
                                                </HowItWorksStep>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </HowItWorksStepsWrapper>
                        </div>
                    </HowItWorksSection>

                    {/* App Section */}
                    <AppSection ref={appRef}>
                        <CSSTransition in={appVisible} timeout={800} classNames="fade" unmountOnExit>
                            {isMobile ? (
                                <>
                                    <SectionBadge>{t('pages.home.app.badge')}</SectionBadge>
                                    <SectionTitle>{t('pages.home.app.section_title')}</SectionTitle>
                                    <SectionSubtitle>
                                        {t('pages.home.app.section_subtitle')}
                                    </SectionSubtitle>

                                    <div {...swipeHandlers}>
                                        <AppSectionBackground>
                                            <AppCardsWrapper activeIndex={activeIndex}>
                                                {appItems.map((item, index) => (
                                                    <AppCard key={item.id}>
                                                        <AppScreen src={item.image} alt={item.title} draggable={false} />
                                                        <AppOverlay>
                                                            <AppTitle>{item.title}</AppTitle>
                                                            <AppDescription>{item.description}</AppDescription>
                                                        </AppOverlay>
                                                    </AppCard>
                                                ))}
                                            </AppCardsWrapper>
                                        </AppSectionBackground>
                                    </div>

                                    <IndicatorWrapper>
                                        {appItems.map((_, index) => (
                                            <IndicatorDot
                                                key={index}
                                                isActive={index === activeIndex}
                                                onClick={() => {
                                                    setActiveIndex(index);
                                                    setIsAppSceneTimerDeactived(true);
                                                }}
                                            />
                                        ))}
                                    </IndicatorWrapper>
                                </>
                            ) : (
                                <>
                                    <AppSectionCard>
                                        <Row className="gx-2">
                                            <Col md={5}>
                                                <SectionBadge>{t('pages.home.app.badge')}</SectionBadge>
                                                <SectionTitle>{t('pages.home.app.section_title')}</SectionTitle>
                                                <SectionSubtitle>
                                                    {t('pages.home.app.section_subtitle')}
                                                </SectionSubtitle>

                                                <ClickableList>
                                                    {appItems.map((item, index) => (
                                                        <ClickableListItem
                                                            key={item.id}
                                                            isActive={index === activeIndex}
                                                            onClick={() => {
                                                                setActiveIndex(index);
                                                                setIsAppSceneTimerDeactived(true);
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center" style={{ marginBottom: 8 }}>
                                                                <ItemTitle>{item.title}</ItemTitle>
                                                                <MaterialIcon icon="chevron_forward" size={24} color={index === activeIndex ? colorPalette.blue500 : colorPalette.textMain} />
                                                            </div>
                                                            {index === activeIndex && (
                                                                <ItemDescription>{item.description}</ItemDescription>
                                                            )}
                                                        </ClickableListItem>
                                                    ))}
                                                </ClickableList>

                                                <AppButtonWrapper>
                                                    <CTAButton
                                                        variant="primary"
                                                        size="L"
                                                        label={t('buttons.all_services')}
                                                        onClick={() => navigate("/details#services")}
                                                        fullWidth
                                                    />
                                                </AppButtonWrapper>
                                            </Col>
                                            <Col md={7} className="text-center">
                                                <AppScreenBackground>
                                                    {appItems.map((item, index) => (
                                                        <AppScreen
                                                            key={item.id}
                                                            src={item.image}
                                                            alt={item.title}
                                                            draggable={false}
                                                            animate={{
                                                                opacity: index === activeIndex ? 1 : 0,
                                                                x: index === activeIndex ? 0 : 50,
                                                            }}
                                                            transition={{ duration: 0.5 }}
                                                            style={{ pointerEvents: index === activeIndex ? 'auto' : 'none' }}
                                                        />
                                                    ))}
                                                </AppScreenBackground>
                                            </Col>
                                        </Row>
                                    </AppSectionCard>
                                </>
                            )}
                        </CSSTransition>
                    </AppSection>

                    {/* FAQ Section */}
                    <FAQSection />

                    {/* Stats Section */}
                    <StatsSection ref={statsRef}>
                        <CSSTransition in={statsVisible} timeout={800} classNames="slide" unmountOnExit>
                            <ManagdInNumbersCard>
                                <Row className="justify-content-between gy-4">
                                    <Col xs={12} md={6} xxl={4}>
                                        <SectionBadge>{t('pages.home.stats.about_us_badge')}</SectionBadge>
                                        <StatsTitle>{t('pages.home.stats.section_title')}</StatsTitle>
                                        <StatsSubtitle>{t('pages.home.stats.section_subtitle')}</StatsSubtitle>
                                        <FeatureList>
                                            <FeatureListItem>
                                                <MaterialIcon icon="devices" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('pages.home.stats.features.digital_platform')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="real_estate_agent" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('pages.home.stats.features.optimized_processes')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="table_chart_view" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('pages.home.stats.features.digital_documentation')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="cheer" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('pages.home.stats.features.personal_managers')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                        </FeatureList>
                                    </Col>

                                    <Col xs={12} md={6} xxl={4}>
                                        <SectionBadge>{t('pages.home.stats.managd_in_numbers_badge')}</SectionBadge>
                                        <Row className="mt-4">
                                            <Col xs={6}>
                                                <StatCard>
                                                    <StatNumber>450+</StatNumber>
                                                    <StatLabel>{t('pages.home.stats.managed_properties')}</StatLabel>
                                                </StatCard>
                                            </Col>
                                            <Col xs={6}>
                                                <StatCard>
                                                    <StatNumber>97%</StatNumber>
                                                    <StatLabel>{t('pages.home.stats.managed_volume')}</StatLabel>
                                                </StatCard>
                                            </Col>
                                            <Col xs={6}>
                                                <StatCard>
                                                    <StatNumber>{t('pages.home.stats.average_yield_value')}</StatNumber>
                                                    <StatLabel>{t('pages.home.stats.average_yield')}</StatLabel>
                                                </StatCard>
                                            </Col>
                                            <Col xs={6}>
                                                <StatCard>
                                                    <StatNumber>92%</StatNumber>
                                                    <StatLabel>{t('pages.home.stats.managed_rents')}</StatLabel>
                                                </StatCard>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </ManagdInNumbersCard>
                        </CSSTransition>
                    </StatsSection>

                    <Articles />

                </Col>
            </Row>

            <Footer />

            {/* Structured Data */}
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqItems.map(item => ({
                    "@type": "Question",
                    "name": item.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": item.answer
                    }
                }))
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                "name": "Managd Property Management LLC",
                "url": "https://www.getmanagd.com",
                "image": "https://www.getmanagd.com/pwa-assets/apple-icon-180.png",
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "1404, Ubora Office Tower, Business Bay",
                    "addressLocality": "Dubai",
                    "addressCountry": "AE"
                },
                "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": 25.1866,
                    "longitude": 55.2617
                },
                "priceRange": "$$",
                "openingHoursSpecification": {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                    "opens": "09:00",
                    "closes": "18:00"
                }
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": t('pages.home.how_it_works.section_title'),
                "description": t('pages.home.how_it_works.section_subtitle'),
                "step": howItWorksSteps.map((step, index) => ({
                    "@type": "HowToStep",
                    "position": index + 1,
                    "name": step.title,
                    "text": step.description
                }))
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Property Management",
                "provider": {
                    "@type": "Organization",
                    "name": "Managd Property Management LLC"
                },
                "areaServed": {
                    "@type": "City",
                    "name": "Dubai"
                },
                "hasOfferCatalog": {
                    "@type": "OfferCatalog",
                    "name": "Property Management Services",
                    "itemListElement": serviceItems.map(item => ({
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": item.title,
                            "description": item.description
                        }
                    }))
                }
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Managd",
                "operatingSystem": "iOS, Android",
                "applicationCategory": "BusinessApplication",
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                },
                "installUrl": [
                    "https://apps.apple.com/de/app/managd/id6745959836",
                    "https://play.google.com/store/apps/details?id=com.getmanagd.app"
                ]
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.getmanagd.com/home"
                    }
                ]
            }} />
        </>
    );
};

export default HomePage;
